import React from "react";

export interface HappeningNowPlatformChipsProps {
  /** Group label for accessibility */
  groupLabel: string;
  androidSelected: boolean;
  iosSelected: boolean;
  onToggleAndroid: () => void;
  onToggleIos: () => void;
  disabled?: boolean;
  /**
   * `"light"` (default) is the white-card treatment used by the old page.
   * `"dark"` drops the white wrapper and flips text/unselected chips to light,
   * for the composer's dark glass panels.
   */
  tone?: "light" | "dark";
}

const chipBase =
  "group relative inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-2xl border-2 px-4 py-2.5 text-sm font-black uppercase tracking-wide outline-none transition-all duration-150 disabled:pointer-events-none disabled:opacity-50 focus-visible:ring-4 focus-visible:ring-[#84D8FF]/50 active:translate-y-1 active:shadow-none";

const chipSelected =
  "border-[#58CC02] bg-[#D7FFB8] text-[#2B7A0B] shadow-[0_5px_0_#58A700] hover:bg-[#C8F8A4]";

const chipUnselected =
  "border-[#E5E5E5] bg-white text-[#777777] shadow-[0_5px_0_#D0D0D0] hover:bg-[#F7F7F7] hover:text-[#4B4B4B]";

const chipUnselectedDark =
  "border-white/15 bg-white/5 text-white/70 shadow-[0_5px_0_rgba(0,0,0,0.3)] hover:bg-white/10 hover:text-white";

const iconWrapBase =
  "grid h-7 w-7 place-items-center rounded-full border-2 transition-colors";

const iconWrapSelected = "border-[#58CC02] bg-white text-[#58CC02]";

const iconWrapUnselected =
  "border-[#E5E5E5] bg-[#F7F7F7] text-[#AFAFAF] group-hover:text-[#777777]";

const iconWrapUnselectedDark =
  "border-white/15 bg-white/10 text-white/50 group-hover:text-white/80";

const checkBase =
  "ml-1 grid h-5 w-5 place-items-center rounded-full text-[11px] font-black transition-all";

const checkSelected = "scale-100 bg-[#58CC02] text-white opacity-100";

const checkUnselected = "scale-75 bg-[#E5E5E5] text-transparent opacity-0";

const checkUnselectedDark = "scale-75 bg-white/15 text-transparent opacity-0";

const AndroidIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M7.2 8.4h9.6a1.8 1.8 0 0 1 1.8 1.8v6.9a1.8 1.8 0 0 1-1.8 1.8h-.5v1.4a1.2 1.2 0 1 1-2.4 0v-1.4H10v1.4a1.2 1.2 0 1 1-2.4 0v-1.4h-.4a1.8 1.8 0 0 1-1.8-1.8v-6.9a1.8 1.8 0 0 1 1.8-1.8Zm-4 2.1a1.2 1.2 0 1 1 2.4 0v5.4a1.2 1.2 0 1 1-2.4 0v-5.4Zm15.2 0a1.2 1.2 0 1 1 2.4 0v5.4a1.2 1.2 0 1 1-2.4 0v-5.4ZM8.35 5.52 7.2 3.53a.55.55 0 0 1 .95-.55l1.22 2.11a6.67 6.67 0 0 1 5.26 0l1.22-2.11a.55.55 0 1 1 .95.55l-1.15 1.99A5.9 5.9 0 0 1 18 7.45H6a5.9 5.9 0 0 1 2.35-1.93ZM9.2 12a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Zm5.6 0a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" />
  </svg>
);

const AppleIcon = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M16.45 12.28c-.02-2.04 1.67-3.03 1.75-3.08-.96-1.4-2.43-1.59-2.95-1.61-1.24-.13-2.45.74-3.08.74-.64 0-1.61-.72-2.66-.7-1.36.02-2.63.8-3.33 2.03-1.44 2.49-.37 6.15 1.01 8.15.69 1 1.5 2.11 2.57 2.07 1.04-.04 1.43-.66 2.68-.66 1.24 0 1.6.66 2.7.64 1.12-.02 1.82-1 2.49-2.01.8-1.14 1.12-2.26 1.13-2.32-.02-.01-2.29-.87-2.31-3.25ZM14.44 6.27c.56-.7.94-1.65.84-2.62-.81.04-1.82.56-2.4 1.24-.52.6-.98 1.59-.86 2.52.91.07 1.85-.46 2.42-1.14Z" />
  </svg>
);

const PlatformChip = ({
  label,
  selected,
  disabled,
  onClick,
  icon,
  dark,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  dark: boolean;
}) => {
  const chipUn = dark ? chipUnselectedDark : chipUnselected;
  const iconUn = dark ? iconWrapUnselectedDark : iconWrapUnselected;
  const checkUn = dark ? checkUnselectedDark : checkUnselected;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={selected}
      className={`${chipBase} ${selected ? chipSelected : chipUn}`}
    >
      <span
        className={`${iconWrapBase} ${selected ? iconWrapSelected : iconUn}`}
      >
        {icon}
      </span>

      <span>{label}</span>

      <span
        aria-hidden="true"
        className={`${checkBase} ${selected ? checkSelected : checkUn}`}
      >
        ✓
      </span>
    </button>
  );
};

/**
 * Toggle chips for Android / iOS. Parent must prevent turning off the last
 * selected platform.
 */
export const HappeningNowPlatformChips: React.FC<
  HappeningNowPlatformChipsProps
> = ({
  groupLabel,
  androidSelected,
  iosSelected,
  onToggleAndroid,
  onToggleIos,
  disabled = false,
  tone = "light",
}) => {
  const dark = tone === "dark";
  return (
    <section
      className={
        dark
          ? "rounded-3xl border-2 border-white/10 bg-transparent py-2"
          : "rounded-3xl border-2 border-[#E5E5E5] bg-[#FFFFFF] py-2"
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <span
            className={`text-sm font-black uppercase tracking-wide ${
              dark ? "text-white/80" : "text-[#4B4B4B]"
            }`}
          >
            {groupLabel}
          </span>

          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-wide ${
              dark ? "bg-white/10 text-white/60" : "bg-[#F7F7F7] text-[#777777]"
            }`}
          >
            Required
          </span>
        </div>

        <div
          className="flex flex-wrap gap-3"
          role="group"
          aria-label={groupLabel}
        >
          <PlatformChip
            label="Android"
            selected={androidSelected}
            disabled={disabled}
            onClick={onToggleAndroid}
            icon={<AndroidIcon className="h-4.5 w-4.5" />}
            dark={dark}
          />

          <PlatformChip
            label="iOS"
            selected={iosSelected}
            disabled={disabled}
            onClick={onToggleIos}
            icon={<AppleIcon className="h-4.5 w-4.5" />}
            dark={dark}
          />
        </div>
      </div>
      <p
        className={`pt-4 text-xs font-bold leading-relaxed ${
          dark ? "text-white/50" : "text-[#777777]"
        }`}
      >
        At least one platform must stay selected.
      </p>
    </section>
  );
};
